/**
 * Costs.jsx
 * What the AI has cost this month.
 *
 * These are the app's own estimates from logged usage, not an invoice — the
 * OpenAI dashboard is the authority, and the screen says so rather than
 * implying a precision it doesn't have.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Database, Film, Warning, Image as ImageIcon, Plus } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function Costs() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .costs()
      .then(setData)
      .catch(setError);
  }, []);

  if (error) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  if (!data) return <p className="mono">Loading…</p>;

  const pct = Math.min(100, (data.totalPence / data.capPence) * 100);
  const text = data.byKind.find((k) => k.kind === 'text');
  const image = data.byKind.find((k) => k.kind === 'image');
  const video = data.byKind.find((k) => k.kind === 'video');

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Costs</div>
          <h1>AI spend this month</h1>
          <p>
            Estimated from logged usage. The OpenAI and Replicate dashboards are the authority —
            Replicate bills in dollars, converted here at a deliberately cautious rate.
          </p>
        </div>
      </div>

      <div className="grid">
        <section className="card">
          <div className="card__head">
            <span className="tile">
              <Database size={21} />
            </span>
            <div className="card__head-text">
              <h2>Total</h2>
              <p>Since {formatLondon(data.monthStart)}</p>
            </div>
          </div>

          <div className="big-number">{gbp(data.totalPence)}</div>
          <div className="meter">
            <div className="meter__fill" style={{ width: `${pct}%` }} />
          </div>
          <p className="row__sub">
            {gbp(data.capPence)} monthly cap · generation stops automatically at the cap
          </p>
        </section>

        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>Where it went</h2>
              <p>Captions cost pennies a post; videos are charged by the second.</p>
            </div>
          </div>

          <div className="rows">
            <div className="row">
              <span className="tile tile--sm">
                <Plus size={17} />
              </span>
              <div>
                <div className="row__label">Captions</div>
                <div className="row__sub">
                  {text?.runs ?? 0} generations ·{' '}
                  {((text?.input_tokens ?? 0) + (text?.output_tokens ?? 0)).toLocaleString()} tokens
                </div>
              </div>
              <div className="row__end">{gbp(text?.pence ?? 0)}</div>
            </div>

            <div className="row">
              <span className="tile tile--sm">
                <Film size={17} />
              </span>
              <div>
                <div className="row__label">Videos</div>
                <div className="row__sub">{video?.runs ?? 0} made with P-Video on Replicate</div>
              </div>
              <div className="row__end">{gbp(video?.pence ?? 0)}</div>
            </div>

            {/* Kept only while past image spend exists — generation was removed. */}
            {(image?.images ?? 0) > 0 && (
              <div className="row">
                <span className="tile tile--sm">
                  <ImageIcon size={17} />
                </span>
                <div>
                  <div className="row__label">Images (no longer generated)</div>
                  <div className="row__sub">{image.images} generated before the feature was removed</div>
                </div>
                <div className="row__end">{gbp(image.pence)}</div>
              </div>
            )}

            {data.byBrand.map((b) => (
              <div className="row" key={b.brand ?? 'unknown'}>
                <div>
                  <div className="row__label">{b.brand ?? 'Deleted brand'}</div>
                  <div className="row__sub">{b.runs} generations</div>
                </div>
                <div className="row__end">{gbp(b.pence)}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Recent generations</h2>
            <p>The last 20, newest first.</p>
          </div>
        </div>

        {data.recent.length === 0 ? (
          <p className="mono">Nothing generated yet.</p>
        ) : (
          <div className="rows">
            {data.recent.map((r, i) => (
              <div className="row" key={i}>
                <span className="badge badge--accent">{r.kind}</span>
                <div>
                  <div className="row__label">{r.brand ?? '—'}</div>
                  <div className="row__sub">
                    {r.model} · {formatLondon(r.created_at)}
                  </div>
                </div>
                <div className="row__end">{gbp(r.cost_pence)}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
